import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { logActivity } from '@/lib/api';
import { toast } from 'sonner';
import {
  Download,
  BookOpen,
  Cpu,
  Users,
  Phone,
  Shield,
  TrendingUp,
  GraduationCap,
  Trophy,
  AlertTriangle,
  FileBarChart,
  FolderOpen,
  Settings,
  Cloud,
  Activity,
  CheckCircle2,
  Zap,
  Layers,
  Database,
  Lock,
  ChevronRight,
  ClipboardCheck,
  Library,
  Search,
  Target,
  Calendar,
  Star,
  Headphones,
  Bell,
  FileText,
  UserCheck,
  Clock,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Brain,
  Server,
  Globe,
  Key,
  GitBranch,
  Box,
  Workflow,
  BarChart3,
  PieChart,
  LineChart,
  Upload,
  HardDrive,
  Link,
  ExternalLink,
  Play,
  Volume2,
  Mail,
  MessageSquare,
  AlertCircle,
  Info,
  Code,
  Terminal,
  Folder,
  Copy,
  ChevronDown,
  Briefcase,
  Building,
  MapPin,
  FileCheck
} from 'lucide-react';

// =============================================================================
// DOCUMENTATION DATA
// =============================================================================

const PLATFORM_VERSION = "2.5.0";
const LAST_UPDATED = "January 24, 2026";

const ARCHITECTURE = {
  overview: `CallSense is a multi-tenant SaaS platform for AI-powered call quality assurance 
and analysis. Built for recruitment/staffing agencies, it provides automated call 
transcription, AI-driven quality scoring, coaching recommendations, and comprehensive 
analytics.`,
  techStack: {
    frontend: ["React 18", "Vite", "Tailwind CSS", "Shadcn/UI", "Recharts", "Lucide Icons"],
    backend: ["FastAPI (Python 3.11)", "Motor (Async MongoDB)", "HTTPX", "Pydantic"],
    database: ["MongoDB 6.0", "44+ Optimized Indexes"],
    ai: ["OpenAI GPT-4.1", "Emergent LLM Integration", "Custom Scoring Engine"],
    integrations: ["Microsoft OneDrive", "CEIPAL ATS", "Azure AD"],
    infrastructure: ["Docker", "Kubernetes", "Nginx", "Supervisor"]
  }
};

const USER_ROLES = [
  {
    role: "Super Admin",
    icon: Shield,
    color: "red",
    description: "Platform-wide administrator with full system access",
    permissions: [
      "Access all tenants and their data",
      "Master delete functionality (permanent deletion)",
      "Azure/OneDrive configuration for all tenants",
      "Billing and subscription management",
      "Global platform settings",
      "View all audit trails across tenants",
      "Manage super admin accounts"
    ],
    restrictions: []
  },
  {
    role: "Tenant Admin (Owner)",
    icon: Building,
    color: "blue",
    description: "Full administrative access within their tenant",
    permissions: [
      "Full access to all tenant features",
      "User management (create, edit, delete users)",
      "QA task assignment and management",
      "Process configuration (call types, scoring)",
      "Scoring guide management",
      "Audit trail access (tenant-scoped)",
      "OneDrive folder configuration",
      "CEIPAL ATS integration setup",
      "Settings and customization"
    ],
    restrictions: [
      "Cannot access other tenants",
      "Cannot perform master delete",
      "Cannot modify Azure/billing settings"
    ]
  },
  {
    role: "Quality Auditor",
    icon: ClipboardCheck,
    color: "purple",
    description: "Reviews calls and provides quality feedback",
    permissions: [
      "View all calls and users within tenant",
      "Add QA notes to any call",
      "Override AI-detected call types",
      "Manage QA task assignments",
      "Access audit trail (read-only)",
      "View analytics and reports",
      "Access call library"
    ],
    restrictions: [
      "Cannot delete users",
      "Cannot modify settings",
      "Cannot access billing/Azure config"
    ]
  },
  {
    role: "User (Recruiter)",
    icon: Users,
    color: "green",
    description: "Standard user with personal dashboard access",
    permissions: [
      "Personal dashboard and call history",
      "View own calls and scores",
      "View QA notes on their calls",
      "Receive notifications for QA feedback",
      "AI Coaching access",
      "Homework AI task completion",
      "Call library access (view only)",
      "Personal KPI tracking"
    ],
    restrictions: [
      "Cannot view other users' calls",
      "Cannot add QA notes",
      "Cannot modify call types",
      "Cannot access admin features"
    ]
  }
];

const FEATURES = [
  {
    category: "Call Management",
    icon: Phone,
    color: "blue",
    items: [
      {
        name: "Call Upload & Processing",
        description: "Upload audio files for automatic transcription and AI analysis",
        details: [
          "Supports MP3, WAV, M4A, and other audio formats",
          "Automatic speaker diarization (caller vs callee)",
          "Real-time transcription with timestamps",
          "Batch upload support via OneDrive sync"
        ]
      },
      {
        name: "AI-Powered Analysis",
        description: "Comprehensive call quality scoring using GPT-4.1",
        details: [
          "18 staffing-specific scoring parameters",
          "Scope-based scoring (parameters vary by call type)",
          "Compliance violation detection",
          "Sentiment and engagement analysis"
        ]
      },
      {
        name: "Call Type Detection",
        description: "Automatic classification into 18 staffing call types",
        details: [
          "Job Outreach, Voicemail, Submission Call",
          "Pre-Screening, Spotlight/Discovery Call",
          "Interview Request/Prep/Debrief",
          "Job Offer, Salary Negotiation, Onboarding",
          "Placement Check-In, Timesheet, Referral Check"
        ]
      },
      {
        name: "Soft Delete & Restore",
        description: "Safe deletion with full recovery capability",
        details: [
          "Soft delete preserves call data",
          "Deleted calls tab for recovery",
          "Restore functionality for accidental deletions",
          "Permanent delete option for admins"
        ]
      }
    ]
  },
  {
    category: "Submissions & ATS Integration",
    icon: Briefcase,
    color: "purple",
    items: [
      {
        name: "CEIPAL ATS Integration",
        description: "Sync candidate submissions from CEIPAL",
        details: [
          "Automatic sync of job postings and submissions",
          "548 submissions synced in current tenant",
          "307 unique jobs with submission activity",
          "Real-time sync status monitoring"
        ]
      },
      {
        name: "Manual Submission Entry",
        description: "Add submissions manually when ATS not available",
        details: [
          "Quick form for candidate details",
          "Resume upload and parsing",
          "Phone number matching with calls",
          "Job information entry"
        ]
      },
      {
        name: "AI Submission Analysis",
        description: "Comprehensive candidate-job fit analysis",
        details: [
          "Resume vs Job requirements matching",
          "Skills gap identification",
          "Call quality integration in analysis",
          "Fit score and recommendation"
        ]
      },
      {
        name: "Call Matching",
        description: "Fuzzy phone number matching with calls",
        details: [
          "Last 8 digits matching for flexibility",
          "Multiple phone format support",
          "Automatic matched calls count",
          "In-popup call playback"
        ]
      }
    ]
  },
  {
    category: "Quality Assurance",
    icon: ClipboardCheck,
    color: "emerald",
    items: [
      {
        name: "QA Notes System",
        description: "Structured feedback from quality auditors",
        details: [
          "Five note types: General, Feedback, Action Required, Positive, Needs Improvement",
          "Permanent notes (audit trail preserved)",
          "Real-time notifications to users",
          "Search and filter capabilities"
        ]
      },
      {
        name: "QA Task Management",
        description: "Assign and track quality review tasks",
        details: [
          "Assign calls to auditors for review",
          "Track review status and completion",
          "Bulk assignment capabilities",
          "Performance metrics per auditor"
        ]
      },
      {
        name: "Scoring Guide Management",
        description: "Configure and customize scoring parameters",
        details: [
          "Multiple scoring configurations",
          "Per-user/role/department assignment",
          "Default scoring guide persistence",
          "Import TW Scoring Guide preset"
        ]
      }
    ]
  },
  {
    category: "AI Coaching",
    icon: Brain,
    color: "orange",
    items: [
      {
        name: "Personalized Coaching Plans",
        description: "AI-generated improvement recommendations",
        details: [
          "Weekly focus areas based on performance",
          "Skill-specific practice drills",
          "Role-play scenarios",
          "Progress tracking"
        ]
      },
      {
        name: "Homework AI",
        description: "360° task tracking system",
        details: [
          "AI-assigned improvement tasks",
          "Evidence submission and review",
          "Completion tracking and analytics",
          "Department and team views"
        ]
      },
      {
        name: "Call Library",
        description: "Learn from best-practice calls",
        details: [
          "Curated collection of excellent calls",
          "Searchable by skill and situation",
          "Audio playback with transcripts",
          "Admin curation tools"
        ]
      }
    ]
  },
  {
    category: "Analytics & Reporting",
    icon: BarChart3,
    color: "cyan",
    items: [
      {
        name: "Dashboard Analytics",
        description: "Real-time performance overview",
        details: [
          "Call volume and trend charts",
          "Score distribution analysis",
          "Top performers leaderboard",
          "Compliance status overview"
        ]
      },
      {
        name: "KPI System",
        description: "Comprehensive performance metrics",
        details: [
          "Weighted scoring (Call Quality 70%, Homework 10%, etc.)",
          "Compliance penalty enforcement",
          "Multi-model configuration",
          "Historical tracking"
        ]
      },
      {
        name: "Team Reports",
        description: "Manager-level team analytics",
        details: [
          "Department comparison",
          "User performance trends",
          "Export to CSV/PDF",
          "Custom date ranges"
        ]
      },
      {
        name: "Audit Trail",
        description: "Complete activity tracking",
        details: [
          "All user actions logged",
          "Filterable by user, action, entity",
          "Detailed event information",
          "Compliance documentation"
        ]
      }
    ]
  },
  {
    category: "Integrations",
    icon: Link,
    color: "indigo",
    items: [
      {
        name: "Microsoft OneDrive",
        description: "Automatic file sync from cloud storage",
        details: [
          "Azure AD authentication",
          "Folder selection and monitoring",
          "Automatic call processing",
          "Sync status and error tracking"
        ]
      },
      {
        name: "CEIPAL ATS",
        description: "Applicant Tracking System integration",
        details: [
          "Jobs and submissions sync",
          "Custom endpoint support",
          "Phone number matching with calls",
          "AI analysis integration"
        ]
      },
      {
        name: "Email Notifications",
        description: "Automated email alerts",
        details: [
          "QA feedback notifications",
          "Review request updates",
          "Task assignment alerts",
          "Configurable templates"
        ]
      }
    ]
  }
];

const API_ENDPOINTS = [
  {
    category: "Authentication & Users",
    endpoints: [
      { method: "POST", path: "/api/auth/login", description: "User login with email/password" },
      { method: "POST", path: "/api/auth/logout", description: "User logout" },
      { method: "GET", path: "/api/tenant/users", description: "List all users in tenant" },
      { method: "POST", path: "/api/tenant/users", description: "Create new user" },
      { method: "PUT", path: "/api/tenant/users/{userId}", description: "Update user details" },
      { method: "DELETE", path: "/api/tenant/users/{userId}", description: "Delete user" },
      { method: "POST", path: "/api/users/import", description: "Bulk import users from CSV" }
    ]
  },
  {
    category: "Calls",
    endpoints: [
      { method: "GET", path: "/api/calls", description: "List calls with filters and pagination" },
      { method: "GET", path: "/api/calls/{id}", description: "Get call details with transcript" },
      { method: "POST", path: "/api/calls/upload", description: "Upload new call recording" },
      { method: "POST", path: "/api/calls/soft-delete", description: "Soft delete calls" },
      { method: "POST", path: "/api/calls/restore", description: "Restore soft-deleted calls" },
      { method: "GET", path: "/api/calls/deleted", description: "List deleted calls" },
      { method: "PUT", path: "/api/calls/{id}/call-type", description: "Override call type" },
      { method: "POST", path: "/api/calls/refresh-call-types", description: "Re-analyze call types" },
      { method: "GET", path: "/api/calls/{id}/audio", description: "Stream call audio" }
    ]
  },
  {
    category: "Submissions",
    endpoints: [
      { method: "GET", path: "/api/submissions", description: "List submissions with filters" },
      { method: "GET", path: "/api/submissions/{id}", description: "Get submission details" },
      { method: "POST", path: "/api/submissions/manual", description: "Create manual submission" },
      { method: "POST", path: "/api/submissions/{id}/analyze", description: "Run AI analysis" },
      { method: "POST", path: "/api/submissions/match-calls", description: "Match submissions to calls" },
      { method: "GET", path: "/api/submissions/config", description: "Get CEIPAL configuration" },
      { method: "POST", path: "/api/submissions/config", description: "Save CEIPAL configuration" },
      { method: "POST", path: "/api/submissions/sync", description: "Trigger CEIPAL sync" },
      { method: "GET", path: "/api/submissions/sync/status", description: "Get sync status" }
    ]
  },
  {
    category: "QA & Scoring",
    endpoints: [
      { method: "GET", path: "/api/qa-notes", description: "List QA notes" },
      { method: "POST", path: "/api/qa-notes", description: "Create QA note" },
      { method: "GET", path: "/api/qa-tasks/assignments", description: "List QA task assignments" },
      { method: "POST", path: "/api/qa-tasks/assign", description: "Assign calls for review" },
      { method: "GET", path: "/api/scoring/configs", description: "List scoring configurations" },
      { method: "POST", path: "/api/scoring/configs", description: "Create scoring config" },
      { method: "PUT", path: "/api/scoring/configs/{id}/default", description: "Set default config" }
    ]
  },
  {
    category: "Analytics",
    endpoints: [
      { method: "GET", path: "/api/analytics/dashboard", description: "Dashboard analytics" },
      { method: "GET", path: "/api/analytics/user/{userId}", description: "User analytics" },
      { method: "GET", path: "/api/analytics/team", description: "Team analytics" },
      { method: "GET", path: "/api/kpi/user/{userId}", description: "User KPI scores" },
      { method: "GET", path: "/api/kpi/leaderboard", description: "KPI leaderboard" }
    ]
  },
  {
    category: "Integrations",
    endpoints: [
      { method: "GET", path: "/api/onedrive/connection-status", description: "OneDrive status" },
      { method: "POST", path: "/api/onedrive/connect", description: "Connect OneDrive" },
      { method: "GET", path: "/api/onedrive/folders", description: "List OneDrive folders" },
      { method: "POST", path: "/api/onedrive/sync", description: "Trigger OneDrive sync" }
    ]
  }
];

const DATA_MODELS = [
  {
    name: "calls",
    description: "Call recordings with transcripts and analysis",
    fields: [
      { name: "id", type: "string", description: "Unique call identifier" },
      { name: "tenant_id", type: "string", description: "Tenant identifier" },
      { name: "user_id", type: "string", description: "Associated user" },
      { name: "filename", type: "string", description: "Original file name" },
      { name: "transcript", type: "array", description: "Transcription segments" },
      { name: "analysis", type: "object", description: "AI analysis results" },
      { name: "average_score", type: "number", description: "Overall quality score" },
      { name: "call_type", type: "string", description: "Detected call type" },
      { name: "call_type_confidence", type: "number", description: "AI confidence %" },
      { name: "is_deleted", type: "boolean", description: "Soft delete flag" },
      { name: "deleted_at", type: "datetime", description: "Deletion timestamp" },
      { name: "created_at", type: "datetime", description: "Upload timestamp" }
    ]
  },
  {
    name: "submissions_cache",
    description: "Candidate submissions from CEIPAL or manual entry",
    fields: [
      { name: "submission_id", type: "string", description: "Unique submission ID" },
      { name: "tenant_id", type: "string", description: "Tenant identifier" },
      { name: "data", type: "object", description: "Submission data (CEIPAL fields)" },
      { name: "phone_normalized", type: "string", description: "Normalized phone for matching" },
      { name: "job_id", type: "string", description: "Associated job code" },
      { name: "matched_calls", type: "array", description: "Matched call IDs" },
      { name: "matched_calls_count", type: "number", description: "Number of matched calls" },
      { name: "analyzed", type: "boolean", description: "AI analysis completed" },
      { name: "analysis_result", type: "object", description: "AI analysis output" },
      { name: "source", type: "string", description: "ceipal or manual" }
    ]
  },
  {
    name: "tenant_users",
    description: "Users within a tenant",
    fields: [
      { name: "id", type: "string", description: "User identifier" },
      { name: "tenant_id", type: "string", description: "Tenant identifier" },
      { name: "email", type: "string", description: "User email" },
      { name: "name", type: "string", description: "Display name" },
      { name: "role", type: "string", description: "User role" },
      { name: "department_id", type: "string", description: "Department reference" },
      { name: "designation_id", type: "string", description: "Designation reference" },
      { name: "is_active", type: "boolean", description: "Account status" }
    ]
  },
  {
    name: "qa_notes",
    description: "Quality assurance notes on calls",
    fields: [
      { name: "id", type: "string", description: "Note identifier" },
      { name: "call_id", type: "string", description: "Associated call" },
      { name: "auditor_id", type: "string", description: "Auditor who created" },
      { name: "note_type", type: "string", description: "general/feedback/action_required/positive/needs_improvement" },
      { name: "content", type: "string", description: "Note content" },
      { name: "created_at", type: "datetime", description: "Creation timestamp" }
    ]
  },
  {
    name: "scoring_configs",
    description: "Scoring configuration templates",
    fields: [
      { name: "id", type: "string", description: "Config identifier" },
      { name: "name", type: "string", description: "Configuration name" },
      { name: "parameters", type: "array", description: "Scoring parameters" },
      { name: "weights", type: "object", description: "Parameter weights" },
      { name: "thresholds", type: "object", description: "Classification thresholds" },
      { name: "is_default", type: "boolean", description: "Default config flag" }
    ]
  }
];

const CALL_TYPES = [
  { name: "Job Outreach", parameters: 12, description: "Initial contact about job opportunity" },
  { name: "Voicemail", parameters: 5, description: "Left voice message, minimal scoring" },
  { name: "Submission Call", parameters: 15, description: "RTR discussion, detailed Q&A" },
  { name: "Pre-Screening", parameters: 10, description: "Manager reconfirms candidate details" },
  { name: "Spotlight/Discovery", parameters: 12, description: "Hiring manager explains job" },
  { name: "Interview Request", parameters: 8, description: "Informing about interview" },
  { name: "Interview Prep", parameters: 10, description: "Coaching for interview" },
  { name: "Interview Debrief", parameters: 10, description: "Post-interview feedback" },
  { name: "Job Offer", parameters: 12, description: "Extending offer, confirming hire" },
  { name: "Counteroffer", parameters: 8, description: "Discussing competing offers" },
  { name: "Salary Negotiation", parameters: 8, description: "Compensation discussions" },
  { name: "Onboarding", parameters: 10, description: "BGI, drug screening, Paycom" },
  { name: "Placement Check-In", parameters: 8, description: "Regular check with placed candidates" },
  { name: "Timesheet Call", parameters: 5, description: "Timesheet follow-ups" },
  { name: "Referral Check", parameters: 8, description: "Reference verification" },
  { name: "Client Introduction", parameters: 10, description: "Introducing candidate to client" },
  { name: "Follow-Up", parameters: 8, description: "General follow-up calls" },
  { name: "Other", parameters: 15, description: "Unclassified calls, full scoring" }
];

// =============================================================================
// COMPONENT HELPERS
// =============================================================================

const MethodBadge = ({ method }) => {
  const colors = {
    GET: "bg-green-500/20 text-green-400 border-green-500/30",
    POST: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    PUT: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    DELETE: "bg-red-500/20 text-red-400 border-red-500/30"
  };
  return (
    <Badge variant="outline" className={`font-mono text-xs ${colors[method]}`}>
      {method}
    </Badge>
  );
};

const SectionHeader = ({ icon: Icon, title, description, color = "blue" }) => (
  <div className="flex items-start gap-4 mb-6">
    <div className={`p-3 rounded-xl bg-${color}-500/10 border border-${color}-500/20`}>
      <Icon className={`h-6 w-6 text-${color}-500`} />
    </div>
    <div>
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
      {description && <p className="text-muted-foreground mt-1">{description}</p>}
    </div>
  </div>
);

const CodeBlock = ({ code, language = "json" }) => (
  <div className="bg-slate-950 rounded-lg p-4 font-mono text-sm overflow-x-auto">
    <pre className="text-slate-300">{code}</pre>
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function Documentation() {
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await logActivity('documentation_download', 'admin', {
        admin_url: adminUrl,
        timestamp: new Date().toISOString(),
        action_type: 'Markdown Download'
      });

      const docContent = generateFullDocumentation();
      const blob = new Blob([docContent], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CallSense_Documentation_v${PLATFORM_VERSION}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Documentation downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download documentation');
    } finally {
      setIsDownloading(false);
    }
  };

  const generateFullDocumentation = () => {
    return `# CallSense Platform Documentation
## Version ${PLATFORM_VERSION} | Last Updated: ${LAST_UPDATED}

---

# Table of Contents

1. [Platform Overview](#platform-overview)
2. [Architecture](#architecture)
3. [User Roles & Permissions](#user-roles--permissions)
4. [Features](#features)
5. [API Reference](#api-reference)
6. [Data Models](#data-models)
7. [Call Types & Scoring](#call-types--scoring)
8. [Integrations](#integrations)
9. [Configuration](#configuration)

---

# Platform Overview

${ARCHITECTURE.overview}

## Technology Stack

### Frontend
${ARCHITECTURE.techStack.frontend.map(t => `- ${t}`).join('\n')}

### Backend
${ARCHITECTURE.techStack.backend.map(t => `- ${t}`).join('\n')}

### Database
${ARCHITECTURE.techStack.database.map(t => `- ${t}`).join('\n')}

### AI/ML
${ARCHITECTURE.techStack.ai.map(t => `- ${t}`).join('\n')}

### Integrations
${ARCHITECTURE.techStack.integrations.map(t => `- ${t}`).join('\n')}

---

# User Roles & Permissions

${USER_ROLES.map(role => `
## ${role.role}

${role.description}

### Permissions
${role.permissions.map(p => `- ${p}`).join('\n')}

${role.restrictions.length > 0 ? `### Restrictions\n${role.restrictions.map(r => `- ${r}`).join('\n')}` : ''}
`).join('\n')}

---

# Features

${FEATURES.map(cat => `
## ${cat.category}

${cat.items.map(item => `
### ${item.name}

${item.description}

${item.details.map(d => `- ${d}`).join('\n')}
`).join('\n')}
`).join('\n')}

---

# API Reference

${API_ENDPOINTS.map(cat => `
## ${cat.category}

| Method | Endpoint | Description |
|--------|----------|-------------|
${cat.endpoints.map(e => `| ${e.method} | \`${e.path}\` | ${e.description} |`).join('\n')}
`).join('\n')}

---

# Data Models

${DATA_MODELS.map(model => `
## ${model.name}

${model.description}

| Field | Type | Description |
|-------|------|-------------|
${model.fields.map(f => `| ${f.name} | ${f.type} | ${f.description} |`).join('\n')}
`).join('\n')}

---

# Call Types & Scoring

The platform supports 18 staffing-specific call types, each with scope-based scoring.

| Call Type | Parameters | Description |
|-----------|------------|-------------|
${CALL_TYPES.map(ct => `| ${ct.name} | ${ct.parameters} | ${ct.description} |`).join('\n')}

---

# Integrations

## Microsoft OneDrive
- Azure AD authentication required
- Automatic file sync from selected folders
- Supports MP3, WAV, M4A audio formats
- Real-time sync status monitoring

## CEIPAL ATS
- Jobs and Submissions sync
- Custom endpoint configuration
- Phone number matching with calls
- AI analysis integration for submissions

---

© 2026 CallSense - AI-Powered Call Quality Platform
`;
  };

  return (
    <div className="p-6 space-y-6" data-testid="documentation-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/20">
              <BookOpen className="h-7 w-7 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Platform Documentation</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">v{PLATFORM_VERSION}</Badge>
                <span className="text-muted-foreground text-sm">Updated {LAST_UPDATED}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64 bg-background"
            />
          </div>
          <Button 
            onClick={handleDownload} 
            disabled={isDownloading}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {isDownloading ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Download className="h-4 w-4 mr-2" /> Download .md</>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50 p-1 flex flex-wrap gap-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Globe className="h-4 w-4 mr-2" />Overview
          </TabsTrigger>
          <TabsTrigger value="architecture" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Layers className="h-4 w-4 mr-2" />Architecture
          </TabsTrigger>
          <TabsTrigger value="roles" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="h-4 w-4 mr-2" />User Roles
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Zap className="h-4 w-4 mr-2" />Features
          </TabsTrigger>
          <TabsTrigger value="api" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Code className="h-4 w-4 mr-2" />API Reference
          </TabsTrigger>
          <TabsTrigger value="data" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Database className="h-4 w-4 mr-2" />Data Models
          </TabsTrigger>
          <TabsTrigger value="calltypes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Phone className="h-4 w-4 mr-2" />Call Types
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border-blue-500/20">
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">What is CallSense?</h3>
                  <p className="text-muted-foreground leading-relaxed">{ARCHITECTURE.overview}</p>
                  
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <div className="text-2xl font-bold text-blue-500">548+</div>
                      <div className="text-sm text-muted-foreground">Submissions Synced</div>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <div className="text-2xl font-bold text-purple-500">18</div>
                      <div className="text-sm text-muted-foreground">Call Types</div>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <div className="text-2xl font-bold text-emerald-500">44+</div>
                      <div className="text-sm text-muted-foreground">DB Indexes</div>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <div className="text-2xl font-bold text-orange-500">GPT-4.1</div>
                      <div className="text-sm text-muted-foreground">AI Engine</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Key Capabilities</h3>
                  <div className="space-y-3">
                    {[
                      { icon: Phone, text: "AI-powered call transcription & analysis" },
                      { icon: Brain, text: "18 staffing-specific call type detection" },
                      { icon: ClipboardCheck, text: "QA notes & task management system" },
                      { icon: Briefcase, text: "CEIPAL ATS integration with phone matching" },
                      { icon: BarChart3, text: "Comprehensive analytics & KPI tracking" },
                      { icon: GraduationCap, text: "Personalized AI coaching & homework" },
                      { icon: Library, text: "Best-practice call library" },
                      { icon: Shield, text: "Full audit trail & compliance" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <item.icon className="h-5 w-5 text-primary" />
                        <span className="text-foreground">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Users, title: "User Management", desc: "Roles, permissions, bulk import", tab: "roles" },
              { icon: Phone, title: "Call Processing", desc: "Upload, analyze, review calls", tab: "features" },
              { icon: Code, title: "API Integration", desc: "REST endpoints documentation", tab: "api" }
            ].map((item, idx) => (
              <Card 
                key={idx} 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setActiveTab(item.tab)}
              >
                <CardContent className="pt-6">
                  <item.icon className="h-8 w-8 text-primary mb-3" />
                  <h4 className="font-semibold text-foreground">{item.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Architecture Tab */}
        <TabsContent value="architecture" className="space-y-6">
          <SectionHeader 
            icon={Layers} 
            title="System Architecture" 
            description="Technical stack and infrastructure overview"
            color="purple"
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(ARCHITECTURE.techStack).map(([category, items]) => (
              <Card key={category} className="bg-card border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-foreground capitalize flex items-center gap-2">
                    {category === 'frontend' && <Globe className="h-4 w-4 text-blue-500" />}
                    {category === 'backend' && <Server className="h-4 w-4 text-green-500" />}
                    {category === 'database' && <Database className="h-4 w-4 text-purple-500" />}
                    {category === 'ai' && <Brain className="h-4 w-4 text-orange-500" />}
                    {category === 'integrations' && <Link className="h-4 w-4 text-cyan-500" />}
                    {category === 'infrastructure' && <Box className="h-4 w-4 text-rose-500" />}
                    {category}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Architecture Diagram Description */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Workflow className="h-5 w-5 text-primary" />
                Data Flow Architecture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4 text-center">
                {[
                  { step: "1", title: "Ingestion", desc: "OneDrive sync or manual upload", icon: Upload },
                  { step: "2", title: "Processing", desc: "Transcription & AI analysis", icon: Cpu },
                  { step: "3", title: "Storage", desc: "MongoDB with 44+ indexes", icon: Database },
                  { step: "4", title: "Delivery", desc: "REST API & React UI", icon: Globe }
                ].map((item, idx) => (
                  <div key={idx} className="relative">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold">
                        {item.step}
                      </div>
                      <item.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                      <h4 className="font-medium text-foreground">{item.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                    </div>
                    {idx < 3 && (
                      <ChevronRight className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-5 w-5 text-muted-foreground z-10" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Roles Tab */}
        <TabsContent value="roles" className="space-y-6">
          <SectionHeader 
            icon={Users} 
            title="User Roles & Permissions" 
            description="Role-based access control system"
            color="emerald"
          />
          
          <div className="grid md:grid-cols-2 gap-6">
            {USER_ROLES.map((role, idx) => (
              <Card key={idx} className="bg-card border-border">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${role.color}-500/10`}>
                      <role.icon className={`h-5 w-5 text-${role.color}-500`} />
                    </div>
                    <div>
                      <CardTitle className="text-foreground">{role.role}</CardTitle>
                      <CardDescription>{role.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Permissions
                    </h4>
                    <ul className="space-y-1">
                      {role.permissions.slice(0, 5).map((perm, pidx) => (
                        <li key={pidx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                          {perm}
                        </li>
                      ))}
                      {role.permissions.length > 5 && (
                        <li className="text-sm text-primary">+{role.permissions.length - 5} more...</li>
                      )}
                    </ul>
                  </div>
                  
                  {role.restrictions.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-orange-500" />
                        Restrictions
                      </h4>
                      <ul className="space-y-1">
                        {role.restrictions.map((rest, ridx) => (
                          <li key={ridx} className="text-sm text-muted-foreground flex items-start gap-2">
                            <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />
                            {rest}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features" className="space-y-6">
          <SectionHeader 
            icon={Zap} 
            title="Platform Features" 
            description="Comprehensive feature documentation"
            color="orange"
          />
          
          <Accordion type="multiple" className="space-y-4">
            {FEATURES.map((category, cidx) => (
              <AccordionItem key={cidx} value={`category-${cidx}`} className="border border-border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${category.color}-500/10`}>
                      <category.icon className={`h-5 w-5 text-${category.color}-500`} />
                    </div>
                    <span className="text-foreground font-semibold">{category.category}</span>
                    <Badge variant="outline" className="ml-2">{category.items.length} features</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-2 gap-4 pt-4">
                    {category.items.map((item, iidx) => (
                      <Card key={iidx} className="bg-muted/30 border-border">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-foreground">{item.name}</CardTitle>
                          <CardDescription className="text-xs">{item.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-1">
                            {item.details.map((detail, didx) => (
                              <li key={didx} className="text-xs text-muted-foreground flex items-start gap-2">
                                <CheckCircle2 className="h-3 w-3 mt-0.5 text-green-500 flex-shrink-0" />
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        {/* API Reference Tab */}
        <TabsContent value="api" className="space-y-6">
          <SectionHeader 
            icon={Code} 
            title="API Reference" 
            description="REST API endpoints documentation"
            color="cyan"
          />
          
          <div className="space-y-6">
            {API_ENDPOINTS.map((category, cidx) => (
              <Card key={cidx} className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Terminal className="h-5 w-5 text-primary" />
                    {category.category}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {category.endpoints.map((endpoint, eidx) => (
                      <div 
                        key={eidx} 
                        className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <MethodBadge method={endpoint.method} />
                        <code className="text-sm font-mono text-foreground flex-1">{endpoint.path}</code>
                        <span className="text-sm text-muted-foreground hidden md:block">{endpoint.description}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Data Models Tab */}
        <TabsContent value="data" className="space-y-6">
          <SectionHeader 
            icon={Database} 
            title="Data Models" 
            description="MongoDB collections and schema documentation"
            color="purple"
          />
          
          <div className="space-y-6">
            {DATA_MODELS.map((model, midx) => (
              <Card key={midx} className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Folder className="h-5 w-5 text-primary" />
                    {model.name}
                  </CardTitle>
                  <CardDescription>{model.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-foreground font-medium">Field</th>
                          <th className="text-left py-2 px-3 text-foreground font-medium">Type</th>
                          <th className="text-left py-2 px-3 text-foreground font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {model.fields.map((field, fidx) => (
                          <tr key={fidx} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="py-2 px-3 font-mono text-foreground">{field.name}</td>
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="text-xs">{field.type}</Badge>
                            </td>
                            <td className="py-2 px-3 text-muted-foreground">{field.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Call Types Tab */}
        <TabsContent value="calltypes" className="space-y-6">
          <SectionHeader 
            icon={Phone} 
            title="Call Types & Scoring" 
            description="18 staffing-specific call classifications"
            color="blue"
          />
          
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Scope-Based Scoring System</CardTitle>
              <CardDescription>
                Each call type has specific scoring parameters. Parameters not applicable to a call type 
                are marked as N/A and excluded from the average score calculation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {CALL_TYPES.map((ct, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors border border-border"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">{ct.name}</h4>
                      <Badge variant="outline" className="text-xs">
                        {ct.parameters} params
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{ct.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border-blue-500/20">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-500" />
                How Call Type Detection Works
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-2xl mb-2">1️⃣</div>
                  <h4 className="font-medium text-foreground mb-1">Transcription</h4>
                  <p className="text-sm text-muted-foreground">
                    Call audio is transcribed using AI speech-to-text
                  </p>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-2xl mb-2">2️⃣</div>
                  <h4 className="font-medium text-foreground mb-1">AI Analysis</h4>
                  <p className="text-sm text-muted-foreground">
                    GPT-4.1 analyzes transcript to detect call type
                  </p>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-2xl mb-2">3️⃣</div>
                  <h4 className="font-medium text-foreground mb-1">Scope Scoring</h4>
                  <p className="text-sm text-muted-foreground">
                    Only applicable parameters are scored for the call type
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium text-foreground mb-2">Override Capability</h4>
                <p className="text-sm text-muted-foreground">
                  Admins and Quality Auditors can override AI-detected call types. All overrides are 
                  logged in the audit trail with the original type, new type, and reason.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="text-center py-6 border-t border-border">
        <p className="text-sm text-muted-foreground">
          © 2026 CallSense - AI-Powered Call Quality Platform | Version {PLATFORM_VERSION}
        </p>
      </div>
    </div>
  );
}
